import { useTranslation } from "react-i18next"

import type { SortDirection, SortOption } from "../hooks/use-file-picker-sorting"
import type { FilePickerSource } from "../types"
import type { GraphFileItem } from "@/api/models"

import { Checkbox } from "@/components/ui/checkbox"

import { FileList } from "./file-list"
import { NavigationBreadcrumb } from "./navigation-breadcrumb"
import { SortingDropdown } from "./sorting-dropdown"

interface FileListWithHeaderProps {
  files: GraphFileItem[]
  selectedMap: Partial<Record<string, GraphFileItem>>
  onToggleSelection: (file: GraphFileItem) => void
  onNavigateToFolder: (folder: GraphFileItem) => void
  onSelectAll: () => void
  onClearSelection: () => void
  tabKey: FilePickerSource
  breadcrumb?: string
  canNavigateBack?: boolean
  onNavigateBack?: () => void
  // Sorting props
  sortOption: SortOption
  sortDirection: SortDirection
  onSortChange: (option: SortOption) => void
  selectionMode?: "single" | "multiple"
}

export function FileListWithHeader({
  files,
  selectedMap,
  onToggleSelection,
  onNavigateToFolder,
  onSelectAll,
  onClearSelection,
  tabKey,
  breadcrumb,
  canNavigateBack = false,
  onNavigateBack,
  sortOption,
  sortDirection,
  onSortChange,
  selectionMode = "multiple",
}: FileListWithHeaderProps) {
  const { t } = useTranslation()
  const selectedCount = Object.keys(selectedMap).length

  return (
    <div className="flex h-full flex-col gap-3 px-1 sm:gap-4 sm:px-0">
      {/* Back button and breadcrumb for navigation */}
      {canNavigateBack && breadcrumb && onNavigateBack && (
        <NavigationBreadcrumb
          breadcrumb={breadcrumb}
          canNavigateBack={canNavigateBack}
          onNavigateBack={onNavigateBack}
        />
      )}

      {/* Header with Select All and Sorting */}
      <div className="flex flex-shrink-0 items-center justify-between border-b pb-2">
        <div className="flex items-center gap-3">
          {selectionMode === "multiple" && (
            <Checkbox
              aria-label={t("filepicker.select_all_aria", { defaultValue: "Select all files" })}
              className="size-3 sm:size-4"
              onCheckedChange={(checked) => {
                if (checked) {
                  onSelectAll()
                } else {
                  onClearSelection()
                }
              }}
            />
          )}
          <h3 className="text-base font-medium sm:text-lg">
            {(() => {
              if (selectedCount > 0) {
                return t("filepicker.count_selected", {
                  defaultValue: "{{count}} selected",
                  count: selectedCount,
                })
              }
              return selectionMode === "single"
                ? t("filepicker.select_files", { defaultValue: "Select a file" })
                : t("filepicker.select_files", { defaultValue: "Select files" })
            })()}
          </h3>
        </div>
        <div className="text-muted-foreground flex items-center gap-2 text-xs">
          <SortingDropdown
            className="mr-2"
            sortDirection={sortDirection}
            sortOption={sortOption}
            onSortChange={onSortChange}
          />
          <span>
            {t("filepicker.items_count", { defaultValue: "{{count}} items", count: files.length })}
          </span>
        </div>
      </div>

      {/* Files List */}
      <div className="flex-1 overflow-auto">
        <FileList
          files={files}
          selectedMap={selectedMap}
          showSource={tabKey === "recent"}
          tabKey={tabKey}
          onNavigateToFolder={onNavigateToFolder}
          onToggleSelection={onToggleSelection}
        />
      </div>
    </div>
  )
}
